import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import theme from '@/config/theme';

interface CompanyBrandingOnboardingProps {
  visible: boolean;
  companyName?: string | null;
  onUpdateCompany: () => void;
  onDismiss: () => void;
}

export const CompanyBrandingOnboarding: React.FC<CompanyBrandingOnboardingProps> = ({
  visible,
  companyName,
  onUpdateCompany,
  onDismiss,
}) => {
  if (!visible) {
    return null;
  }

  const resolvedCompanyName = companyName?.trim() || 'sua empresa';

  return (
    <LinearGradient colors={theme.colors.secondary.gradient} style={styles.card}>
      <View style={styles.headerRow}>
        <View style={styles.titleBlock}>
          <Text style={styles.pill}>Próximo passo</Text>
          <Text style={styles.title}>Deixe o checklist com a cara da sua empresa</Text>
        </View>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Fechar dica de personalização"
        >
          <Ionicons name="close" size={18} color={theme.colors.secondary.contrast} />
        </TouchableOpacity>
      </View>

      <Text style={styles.description}>
        Atualize logo, nome, endereço e contatos para que os PDFs e relatórios fiquem profissionais para clientes,
        seguradoras e assistências. Se preferir, toque em{' '}
        <Ionicons name="settings-outline" size={14} color="rgba(255,255,255,0.95)" /> Config &gt; Empresa para fazer isso depois.
      </Text>

      <View style={styles.actionsRow}>
        <TouchableOpacity style={styles.primaryButton} onPress={onUpdateCompany}>
          <Text style={styles.primaryButtonText}>Atualizar agora</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.secondaryButton} onPress={onDismiss}>
          <Text style={styles.secondaryButtonText}>Depois</Text>
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    ...theme.shadows.md,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  pill: {
    backgroundColor: 'rgba(0,0,0,0.15)',
    color: theme.colors.secondary.contrast,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 2,
    borderRadius: theme.borderRadius.pill,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
    alignSelf: 'flex-start',
  },
  title: {
    marginTop: theme.spacing.xs,
    color: theme.colors.secondary.contrast,
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.semibold as any,
    lineHeight: 24,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  description: {
    marginTop: theme.spacing.sm,
    color: 'rgba(255,255,255,0.92)',
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.md,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: theme.colors.secondary.contrast,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    color: theme.colors.secondary.default,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  secondaryButton: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    color: theme.colors.secondary.contrast,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
});

export default CompanyBrandingOnboarding;
